import { Button, Menu, MenuItem, SxProps } from '@mui/material';
import {
  PoiStatus,
  PointOfInterestsCountByStatusDocument,
  useUpdatePointOfInterestsStatusMutation,
} from '@/__generated__/graphql';
import { SwapHoriz as UpdateStatusIcon } from '@mui/icons-material';
import { useMemo, useState } from 'react';
import { usePoiStatusLabels } from '@/hooks/usePoiStatusLabels';
import { abyssColors, white } from '@/theme/colors';
import { Theme } from '@emotion/react';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';

type Props = {
  selectedPointOfInterests: string[];
  refetchPointOfInterests: () => void;
  disabled?: boolean;
  poiType?: string;
};

export const StatusChangeMenu = ({
  selectedPointOfInterests,
  refetchPointOfInterests,
  disabled,
  poiType,
}: Props) => {
  const [anchorElement, setAnchorElement] = useState<HTMLElement | undefined>(undefined);
  const [buttonStyle, setButtonStyle] = useState<SxProps<Theme>>({});
  const { getStatusOptions } = usePoiStatusLabels();

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const [updatePointOfInterestsStatus, { loading }] = useUpdatePointOfInterestsStatusMutation();

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
    setButtonStyle({ backgroundColor: abyssColors.primary[700], color: white });
  };

  const handleClose = () => {
    setAnchorElement(undefined);
    setButtonStyle({});
  };

  const handleChangeStatus = (status: PoiStatus) => {
    handleClose();
    updatePointOfInterestsStatus({
      variables: {
        input: { pointOfInterestIds: selectedPointOfInterests, status },
      },
      refetchQueries: [PointOfInterestsCountByStatusDocument],
    })
      .then(() => {
        refetchPointOfInterests();
        showSnackBar('POI status has been updated successfully', 'success');
      })
      .catch(() => {
        showSnackBar('Failed to update POI status', 'error');
      });
  };

  const statusOptions = useMemo(() => {
    if (!poiType) return [];
    return getStatusOptions(poiType);
  }, [getStatusOptions, poiType]);

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={loading ? <></> : <UpdateStatusIcon />}
        sx={buttonStyle}
        disabled={disabled || loading}
        onClick={handleMenuClick}
      >
        {loading ? <>Updating...</> : <>Change POI Status</>}
      </Button>

      <Menu
        sx={{ mt: 0.5 }}
        anchorEl={anchorElement}
        open={Boolean(anchorElement)}
        onClose={handleClose}
      >
        {statusOptions.map((option) => (
          <MenuItem
            sx={{ width: 180 }}
            key={option.value}
            value={option.value}
            onClick={() => handleChangeStatus(option.value as PoiStatus)}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
